import { FoldedCollection, FoldedResult, Result } from '@coveo/headless';
import { ResultLink } from '../ResultLink';
import { HighlightedText } from '../HighlightedText';
import { Grid } from '@mui/material';
import React from 'react';
import { PersonalizedFieldNames } from 'Feature/Coveo/Models/CoveoModels';
import { getCoveoFieldNameByString } from 'Feature/Coveo/Mappers/CoveoFieldMapper';

const maxShownPageChildren = 3;
export interface IPageResultsProps {
  collection: FoldedCollection;
}
export function PageResults({ collection }: IPageResultsProps) {
  function getDate(result: Result, fieldName: string) {
    if (result.raw.hasOwnProperty(fieldName)) {
      const date: Date = new Date(result.raw[fieldName] as string);
      return date.toLocaleDateString();
    }
    return '';
  }

  function renderDates(result: Result) {
    return (
      <Grid container className="result-date caption">
        {getDate(result, 'updated') && <Grid size={{ xs: 6 }}>{'Published Date: ' + getDate(result, 'updated')}</Grid>}
      </Grid>
    );
  }

  function renderResult(result: Result) {
    return <span key={result.uniqueId}> ... {displayExcerpt(result)}</span>;
  }

  function displayExcerpt(result: Result) {
    return <HighlightedText value={result.excerpt} highlights={result.excerptHighlights} />;
  }
  // The below two methods seem the same, but one is an array of results and the other is an array of objects that has a result property, without re-mapping to another array
  // this may be the most efficient way to handle it
  let shownComponents: string[] = [];
  function renderResults(results: Result[], parentResult: Result) {
    addShownComponent(parentResult);
    return results.map((result) => {
      return renderFoldedResult(result);
    });
  }
  function renderFoldedResults(results: FoldedResult[], parentResult: Result) {
    addShownComponent(parentResult);
    return results.map(({ result }) => {
      return renderFoldedResult(result);
    });
  }
  function renderFoldedResult(result: Result) {
    // Make sure that two sxa items from the same component don't get shown in search results. This limits the noise in results
    const componentGuid: string = getResultProperty(
      getCoveoFieldNameByString(PersonalizedFieldNames.componentguid)?.coveoFieldName!,
      result,
    );
    if (
      (shownComponents.length <= maxShownPageChildren || result.excerptHighlights.length !== 0) &&
      !shownComponents.includes(componentGuid)
    ) {
      addShownComponent(result);
      return renderResult(result);
    }
    return <React.Fragment key={result.uniqueId}></React.Fragment>;
  }
  function addShownComponent(result: Result) {
    const componentGuid: string = getResultProperty(
      getCoveoFieldNameByString(PersonalizedFieldNames.componentguid)?.coveoFieldName!,
      result,
    );
    if (componentGuid !== '') shownComponents.push(componentGuid);
  }
  function getResultProperty(propertyName: string, result: Result): string {
    return result.raw.hasOwnProperty(propertyName) ? (result.raw[propertyName] as string) : '';
  }
  return (
    <article className="result-folding-result">
      <div className="result-folding-title">
        <ResultLink result={collection.result}>
          <HighlightedText value={collection.result.title} highlights={collection.result.titleHighlights} />
        </ResultLink>
      </div>
      <div className="result-folding-excerpts caption">
        <ul>
          <li>
            <article>
              <span>{displayExcerpt(collection.result)}</span>
              {
                // This ugly code exists because we want to group results that fall under the same page. We are using the Coveo result folding for this
                // Result folding relies on grouping by foldingcollection, then grouping by foldingparent. The parent won't always be returned in results
                // so the default behavior is to group by collection which functions how we more or less want but children array is empty because no official parent was returned.
                // Coveo picks a random child to be the parent and puts all other children under childResults property, but since its not entirely intended functionality it
                // is not typed. Some day they will have a "product grouping" feature that would more generically allow grouping of indexed items.
                // If children array has values, then we can utilize the true folding concept
                collection.children.length === 0
                  ? renderResults((collection.result as any)['childResults'] as Result[], collection.result)
                  : renderFoldedResults(collection.children, collection.result)
              }
            </article>
          </li>
        </ul>
      </div>
      {renderDates(collection.result)}
    </article>
  );
}
